import React, { useState } from "react";
import { Link, useParams } from "react-router";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  AlertTriangle as AlertTriangleIcon,
  RefreshCw,
  AlertTriangle,
  ExternalLink,
  Target,
  Copy,
  Check,
  Activity,
} from "react-feather";
import { useGet, usePost } from "../../api";
import {
  FullAutoCouponsTargetingCheckStatus,
  IFullAutoCouponsTargetingDetailResponse,
  IFullAutoCouponsTargetingEligibilityCheck,
  IFullAutoCouponsTargetingSqlCoupon,
  KindredPublishStatus,
} from "../../interfaces";
import {
  TargetingPanel,
  CouponWithStatus,
  SdkTestResultsCard,
} from "../../components/workbench";
import { SDKTestModal } from "./sdk-test-modal";

const StatusBadge: React.FC<{
  status: FullAutoCouponsTargetingCheckStatus;
  size?: "sm" | "lg";
}> = ({ status, size = "sm" }) => {
  const iconSize = size === "lg" ? 14 : 10;
  if (status === "green") {
    return (
      <Badge bg="success" className={size === "lg" ? "fs-6" : ""}>
        <CheckCircle size={iconSize} /> OK
      </Badge>
    );
  }
  if (status === "amber") {
    return (
      <Badge bg="warning" text="dark" className={size === "lg" ? "fs-6" : ""}>
        <AlertTriangleIcon size={iconSize} /> Pending
      </Badge>
    );
  }
  return (
    <Badge bg="danger" className={size === "lg" ? "fs-6" : ""}>
      <XCircle size={iconSize} /> Missing
    </Badge>
  );
};

const overallLabel = (s: FullAutoCouponsTargetingCheckStatus): string => {
  if (s === "green") return "All checks pass";
  if (s === "amber") return "DB checks pass — SDK end-to-end test required";
  return "Missing — something is completely absent";
};

/** Build a CouponWithStatus from an IFullAutoCouponsTargetingSqlCoupon so
 *  the existing TargetingPanel can be reused without changes. */
function toTargetingCoupon(
  sql: IFullAutoCouponsTargetingSqlCoupon,
  brandId: string | null,
  brandName: string | null,
  domain: string
): CouponWithStatus {
  return {
    id: sql.couponId,
    code: sql.code,
    brandId: brandId ?? "",
    brandName: brandName ?? "",
    discount: 0,
    summary: null,
    validFrom: sql.validFrom ?? "",
    validTo: sql.validTo ?? "",
    source: "",
    publishedStatus: sql.publishedStatus,
    priority: sql.priority,
    coverage: parseInt(sql.coverage ?? "0", 10) || 0,
    countries: sql.countriesEnabled,
    couponDomains: [domain],
    campaignDomains: [],
    isTopBrand: false,
    deactivatedAt: null,
    _status: sql.isLive ? "LIVE" : "EXPIRED",
  };
}

export const FullAutoCouponsTargetingDetail: React.FC = () => {
  const { qaItemId = "" } = useParams<{ qaItemId: string }>();
  const [targetingCoupon, setTargetingCoupon] =
    useState<IFullAutoCouponsTargetingSqlCoupon | null>(null);
  const [sdkTestOpen, setSdkTestOpen] = useState(false);
  const [copiedDeal, setCopiedDeal] = useState(false);
  const [runningItem, setRunningItem] = useState(false);
  const { data, loading, error, refetch } =
    useGet<IFullAutoCouponsTargetingDetailResponse>(
      `full-auto-coupons-targeting/detail/${qaItemId}`
    );
  const { execute: runItemApi } = usePost<{ status: string }>();

  const runLive = async () => {
    setRunningItem(true);
    try {
      await runItemApi("full-auto-coupons-targeting/runs/item", {
        qaItemId,
      });
    } catch {
      // fire-and-forget 202; errors are logged server-side
    } finally {
      setRunningItem(false);
    }
    // Backend acknowledged — the run itself is async on the server;
    // refresh to pick up the new snapshot once it lands.
    refetch();
  };

  if (loading && !data) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <Alert variant="danger">
        Failed to load detail.{" "}
        <Link to="/kindred/full-auto-coupons-targeting">Back</Link>
      </Alert>
    );
  }

  const { api, sqlCoupons, eligibilityChecks, snapshot } = data;
  const liveCoupons = sqlCoupons.filter((c) => c.isLive);

  const copyDealJson = () => {
    const text = JSON.stringify(api.deal ?? null, null, 2);
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
    } else {
      const el = document.createElement("textarea");
      el.value = text;
      el.style.position = "fixed";
      el.style.opacity = "0";
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopiedDeal(true);
    setTimeout(() => setCopiedDeal(false), 2000);
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <Link
            to="/kindred/full-auto-coupons-targeting"
            className="text-decoration-none small"
          >
            <ArrowLeft size={14} className="me-1" />
            Back
          </Link>
          <h4 className="mb-0 mt-1">
            {data.domain}{" "}
            <Badge bg="secondary" className="align-middle">
              {data.countryCode}
            </Badge>
          </h4>
          <div className="text-muted small">
            {data.brandId && (
              <>
                Brand:{" "}
                <Link to={`/kindred/brands/${data.brandId}`}>
                  {data.brandName ?? data.brandId.slice(0, 8)}
                </Link>
              </>
            )}
          </div>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <span title={overallLabel(data.overallStatus)}>
            <StatusBadge status={data.overallStatus} size="lg" />
          </span>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => refetch()}
            disabled={loading}
          >
            <RefreshCw size={14} className="me-1" />
            Refresh
          </Button>
          <Button
            size="sm"
            variant="outline-info"
            onClick={() => setSdkTestOpen(true)}
            disabled={loading}
            title="Record an SDK end-to-end test result"
          >
            <Activity size={14} className="me-1" />
            SDK Test
          </Button>
          <Button
            size="sm"
            variant="outline-primary"
            onClick={runLive}
            disabled={loading || runningItem}
            title="Queue a serverless re-run for this item and persist as today's snapshot"
          >
            {runningItem ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : (
              <RefreshCw size={14} className="me-1" />
            )}
            Run live
          </Button>
        </div>
      </div>

      {/* Eligibility breakdown */}
      <Card className="mb-3">
        <Card.Header className="py-2 small fw-bold">
          Eligibility breakdown (live)
        </Card.Header>
        <Card.Body className="p-0">
          <Table size="sm" className="mb-0">
            <tbody>
              {eligibilityChecks.map(
                (c: IFullAutoCouponsTargetingEligibilityCheck) => (
                  <tr key={c.key}>
                    <td style={{ width: 100 }}>
                      <StatusBadge status={c.status} />
                    </td>
                    <td style={{ width: 240 }} className="fw-semibold">
                      {c.label}
                    </td>
                    <td className="text-muted small">{c.detail}</td>
                  </tr>
                )
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* SDK test results pulled through from the snapshot */}
      {snapshot.metrics.lastSdkTest && (
        <SdkTestResultsCard test={snapshot.metrics.lastSdkTest} />
      )}

      {/* Snapshot provenance */}
      <Alert variant="info" className="py-2 small mb-3">
        <strong>Snapshot</strong> {snapshot.snapshotDate} ({snapshot.runId})
        {data.live && (
          <Badge bg="warning" text="dark" className="ms-2">
            Live re-run
          </Badge>
        )}
        {" \u2014 "}activeCoupons={snapshot.metrics.activeCouponCount}, autoTpl=
        {snapshot.metrics.autoTemplateCount}, skuTpl=
        {snapshot.metrics.skuTemplateCount}, deal=
        {String(snapshot.metrics.hasDeal)}, offers=
        {snapshot.metrics.offerCount}.
        {snapshot.errors?.length > 0 && (
          <div className="mt-1">
            <AlertTriangle size={12} /> Snapshot errors:{" "}
            {snapshot.errors.join("; ")}
          </div>
        )}
      </Alert>
      {api.errors.length > 0 && (
        <Alert variant="warning" className="py-2 small mb-3">
          <AlertTriangle size={12} /> Capture errors: {api.errors.join("; ")}
        </Alert>
      )}

      <Row className="g-3">
        {/* Live API: deal */}
        <Col md={6}>
          <Card className="h-100">
            <Card.Header className="py-2 small fw-bold d-flex justify-content-between align-items-center">
              <span>Deal (DB)</span>
              <Button
                size="sm"
                variant={copiedDeal ? "outline-success" : "outline-secondary"}
                className="py-0 px-1"
                style={{ fontSize: 11 }}
                onClick={copyDealJson}
                title="Copy full deal JSON to clipboard"
              >
                {copiedDeal ? (
                  <>
                    <Check size={11} className="me-1" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy size={11} className="me-1" />
                    Copy JSON
                  </>
                )}
              </Button>
            </Card.Header>
            <Card.Body className="small">
              {api.deal ? (
                <dl className="row mb-0">
                  <dt className="col-4">Brand</dt>
                  <dd className="col-8">{api.deal.brandName}</dd>
                  <dt className="col-4">Cashback</dt>
                  <dd className="col-8" title={api.deal.id}>
                    {api.deal.totalCommission ?? "—"}{" "}
                    <span className="text-muted">{api.deal.cashbackType}</span>
                  </dd>
                  <dt className="col-4">Source</dt>
                  <dd className="col-8">{api.deal.source}</dd>
                  <dt className="col-4">Window</dt>
                  <dd className="col-8">
                    {api.deal.startDate?.slice(0, 10)} →{" "}
                    {api.deal.endDate?.slice(0, 10)}
                  </dd>
                </dl>
              ) : (
                <span className="text-muted">No deal returned.</span>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Live API: autocoupons summary */}
        <Col md={6}>
          <Card className="h-100">
            <Card.Header className="py-2 small fw-bold">
              Autocoupons API (live)
            </Card.Header>
            <Card.Body className="small">
              <Table size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Template type</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  <tr title="Templates returned by /autocoupons/v1/checkout when called WITHOUT an encrypted SKU/basket payload. These power the auto-coupon flow that runs without knowing the user's basket.">
                    <td>Auto Coupon Templates</td>
                    <td>{api.autoTemplates.length}</td>
                  </tr>
                  <tr title="Templates returned by /autocoupons/v1/checkout when called WITH an encrypted SKU/basket payload. These power coupon eligibility that depends on the user's basket contents.">
                    <td>SKU / Basket Templates</td>
                    <td>{api.skuTemplates.length}</td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Combined LIVE coupon list — SQL is the source of truth, with a flag
          indicating whether the autocoupons API also returned the code. Any
          coupon returned by the API but not present in our LIVE SQL set is
          appended at the bottom (API-only). */}
      <Card className="mt-3">
        <Card.Header className="py-2 small fw-bold d-flex justify-content-between">
          <span>
            LIVE coupons for {data.domain}/{data.countryCode} (
            {liveCoupons.length})
          </span>
          {data.brandId && (
            <Link
              to={`/kindred/coupons?brandId=${data.brandId}`}
              className="small"
            >
              View all in Kindred Coupons <ExternalLink size={10} />
            </Link>
          )}
        </Card.Header>
        <Card.Body className="p-0">
          {liveCoupons.length === 0 ? (
            <div className="text-muted small p-3">
              No LIVE coupons for {data.domain}/{data.countryCode}.
            </div>
          ) : (
            <div style={{ maxHeight: 600, overflow: "auto" }}>
              <Table size="sm" className="mb-0 small">
                <thead
                  className="table-light"
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  <tr>
                    <th>Pri</th>
                    <th>Code</th>
                    <th>Sitewide</th>
                    <th>Coverage</th>
                    <th>Metadata</th>
                    <th>Coverage match</th>
                    <th>Targeting</th>
                    <th>Valid to</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {[...liveCoupons]
                    .sort((a, b) => a.priority - b.priority)
                    .map((sql) => (
                      <tr key={sql.couponId}>
                        <td className="text-muted">{sql.priority}</td>
                        <td>
                          <Link
                            to={`/kindred/coupons/${sql.couponId}`}
                            className="text-decoration-none"
                          >
                            {sql.code}
                          </Link>
                        </td>
                        <td>{sql.coverage === "1" ? "Yes" : "No"}</td>
                        <td>{sql.coverage ?? "—"}</td>
                        <td>
                          {sql.hasMetadata ? (
                            <Badge bg="success">Yes</Badge>
                          ) : (
                            <Badge bg="danger">No</Badge>
                          )}
                          {sql.hasMetadata && sql.metadataCoverage && (
                            <span className="text-muted ms-1">
                              ({sql.metadataCoverage})
                            </span>
                          )}
                        </td>
                        <td>
                          {sql.hasMetadata ? (
                            sql.coverageMatchesMetadata ? (
                              <Badge bg="success">Match</Badge>
                            ) : (
                              <Badge bg="danger">Mismatch</Badge>
                            )
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>
                          {sql.hasTargeting ? (
                            <Badge bg="success">{sql.targetingCount}</Badge>
                          ) : (
                            <Badge bg="danger">0</Badge>
                          )}
                        </td>
                        <td className="text-muted">
                          {sql.validTo ? sql.validTo.slice(0, 10) : "—"}
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            className="py-0 px-1"
                            style={{ fontSize: 11 }}
                            title="Manage targeting for this coupon"
                            onClick={() => setTargetingCoupon(sql)}
                          >
                            <Target size={11} className="me-1" />
                            Targeting
                          </Button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <div className="text-muted small mt-2">
        Live snapshot fetched at {new Date(data.fetchedAt).toLocaleString()}.
      </div>

      {/* SDK test modal */}
      <SDKTestModal
        show={sdkTestOpen}
        itemId={qaItemId}
        domain={data.domain}
        countryCode={data.countryCode}
        onClose={() => setSdkTestOpen(false)}
        onSaved={() => refetch()}
      />

      {/* Targeting edit modal */}
      <Modal
        show={!!targetingCoupon}
        onHide={() => setTargetingCoupon(null)}
        fullscreen
      >
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">
            Targeting — <code>{targetingCoupon?.code}</code>
            <span className="text-muted ms-2 small">
              {data.domain} / {data.countryCode}
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-2">
          {targetingCoupon && (
            <TargetingPanel
              domain={data.domain}
              liveCoupons={[
                toTargetingCoupon(
                  targetingCoupon,
                  data.brandId,
                  data.brandName,
                  data.domain
                ),
              ]}
              initialCouponId={targetingCoupon.couponId}
              enabled
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};
